import { clamp, fbm2, noise2, smoothstep } from '../core/noise';
import {
  BIOME_NAMES,
  BIOME_PROFILES,
  biomeWeightsAt,
  type BiomeColor,
  type BiomeTerrainProfile,
  type BiomeWeights,
} from './biomes';

export const MOUNTAIN_GRADE = 0.24;
export const START_X = 0;
export const START_Z = -1_200;

function blendedTerrainProfile(weights: BiomeWeights): BiomeTerrainProfile {
  let rolling = 0;
  let ridges = 0;
  let drifts = 0;
  let microRoughness = 0;

  for (const name of BIOME_NAMES) {
    const amount = weights[name];
    const terrain = BIOME_PROFILES[name].terrain;
    rolling += terrain.rolling * amount;
    ridges += terrain.ridges * amount;
    drifts += terrain.drifts * amount;
    microRoughness += terrain.microRoughness * amount;
  }

  return { rolling, ridges, drifts, microRoughness };
}

function blendedColor(
  weights: BiomeWeights,
  select: (name: (typeof BIOME_NAMES)[number]) => BiomeColor,
): [number, number, number] {
  const color: [number, number, number] = [0, 0, 0];
  for (const name of BIOME_NAMES) {
    const source = select(name);
    color[0] += source[0] * weights[name];
    color[1] += source[1] * weights[name];
    color[2] += source[2] * weights[name];
  }
  return color;
}

function mixColor(
  target: [number, number, number],
  source: readonly [number, number, number],
  amount: number,
): void {
  target[0] += (source[0] - target[0]) * amount;
  target[1] += (source[1] - target[1]) * amount;
  target[2] += (source[2] - target[2]) * amount;
}

export function terrainHeight(
  x: number,
  z: number,
  weights: BiomeWeights = biomeWeightsAt(x, z),
): number {
  const profile = blendedTerrainProfile(weights);

  // The mountain falls consistently along z. Keeping the large-scale profile
  // one-dimensional means the skier can traverse indefinitely in x without
  // meeting valley walls or being pushed back toward a course center.
  const longRoll = fbm2(z / 460, 8.7, 4, 1301) * 18;
  const rolling = fbm2(z / 128, -5.3, 4, 1409) * 8.5;
  const bumpWarp = noise2(x / 340, z / 310, 1451) * 22;
  const rollingBumps = fbm2((z + bumpWarp) / 58, 3.4, 4, 1471) * 6.4;

  // Interlocking ridges and bowls give the run a genuinely mountainous
  // silhouette in every direction. Their long wavelengths keep them fun to
  // carve across instead of turning the snowfield into visual chatter.
  const ridgeWarpX = noise2(x / 520, z / 470, 1493) * 110;
  const ridgeWarpZ = noise2(x / 410, z / 560, 1499) * 95;
  const broadHills = fbm2((x + ridgeWarpX) / 255, (z + ridgeWarpZ) / 230, 5, 1501) * 18;
  const foldedRidges = (1 - Math.abs(noise2((x - ridgeWarpX * 0.35) / 150, z / 205, 1507))) * 8.5;

  // These globally sampled forms are smoothly revealed by the biome weights.
  // Lanternwood gets pillowy powder, Blue Ice long glacial swells, and the
  // timberline stronger cross-slope snow fingers rather than hard seams.
  const lanternPillows = fbm2((x + bumpWarp) / 76, z / 88, 4, 1523) * 2.4;
  const iceWarp = noise2(x / 430, z / 510, 1531) * 76;
  const glacialSwells = fbm2((x + iceWarp) / 185, z / 285, 4, 1543) * 5.2;
  const timberFingers = (
    0.56 - Math.abs(noise2((x - ridgeWarpX * 0.28) / 92, z / 245, 1549))
  ) * 5.4;

  // A little two-dimensional texture keeps the snow alive under the skis,
  // but stays much smaller than the downhill terrain profile.
  const driftWarp = fbm2(z / 280, 12.1, 3, 1511) * 20;
  const broadDrifts = fbm2((x + driftWarp) / 58, z / 68, 4, 1553);
  const skiBumps = fbm2((x - driftWarp * 0.35) / 21, z / 25, 3, 1579);
  const windDrift = Math.sin((x * 0.5 + z + driftWarp) / 17) * 0.48;
  const timberSastrugi = Math.sin((x * 0.86 + z * 0.25 + driftWarp) / 8.5) * 0.44;

  return z * MOUNTAIN_GRADE
    + longRoll
    + (rolling + rollingBumps) * profile.rolling
    + (broadHills + foldedRidges) * profile.ridges
    + lanternPillows * weights.lanternwood
    + glacialSwells * weights.blueIce
    + timberFingers * weights.timberline
    + broadDrifts * 2.1 * profile.drifts
    + skiBumps * 0.72 * profile.microRoughness
    + windDrift * profile.drifts
    + timberSastrugi * weights.timberline;
}

export function terrainGradient(x: number, z: number, distance = 0.7): { x: number; z: number } {
  const dx = (terrainHeight(x + distance, z) - terrainHeight(x - distance, z)) / (distance * 2);
  const dz = (terrainHeight(x, z + distance) - terrainHeight(x, z - distance)) / (distance * 2);
  return { x: dx, z: dz };
}

export function terrainColor(
  x: number,
  z: number,
  height: number,
  weights: BiomeWeights = biomeWeightsAt(x, z),
): readonly [number, number, number] {
  const color = blendedColor(weights, (name) => BIOME_PROFILES[name].terrainColor);
  const shadow = blendedColor(weights, (name) => BIOME_PROFILES[name].shadowColor);
  const rock = blendedColor(weights, (name) => BIOME_PROFILES[name].rockColor);

  const variation = noise2(x / 17, z / 17, 2111) * 0.014;
  const relativeHeight = height - z * MOUNTAIN_GRADE;
  const exposed = smoothstep(7, 16, relativeHeight + noise2(x / 34, z / 37, 2161) * 3.5);
  const windWarp = noise2(x / 115, z / 135, 2237) * 17;
  const windRipple = Math.sin((x * 0.34 + z + windWarp) / 13.5);
  const windCrust = smoothstep(0.7, 0.96, windRipple) * (0.016 + weights.timberline * 0.022);
  const hollow = smoothstep(0.34, 0.82, -noise2(x / 68, z / 61, 2281));
  const shadowAmount = hollow * (0.08 + weights.blueIce * 0.12 + weights.timberline * 0.035);
  mixColor(color, shadow, shadowAmount);

  // Thin blue flow seams make the glacier recognizable without introducing
  // physical holes or discontinuities in the skiable surface.
  const seamWarp = noise2(x / 170, z / 210, 2311) * 28;
  const seamWave = Math.abs(Math.sin((x + seamWarp + z * 0.16) / 14));
  const iceSeam = smoothstep(0.94, 0.995, seamWave) * weights.blueIce * 0.24;
  mixColor(color, BIOME_PROFILES.blueIce.shadowColor, iceSeam);

  // High timberline faces reveal granite; the weighted mask lets exposure
  // fade naturally as the terrain enters the neighboring snowfields.
  const stoneAmount = exposed * weights.timberline * 0.28;
  mixColor(color, rock, stoneAmount);

  color[0] += variation + windCrust;
  color[1] += variation + windCrust * 1.03;
  color[2] += variation + windCrust * 1.06;
  return [clamp(color[0], 0, 1), clamp(color[1], 0, 1), clamp(color[2], 0, 1)];
}
